import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import CreerPatientModal from '../../../components/feature/CreerPatientModal';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useOfflineMutation } from '../../../hooks/useOfflineMutation';

interface PatientInfo {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string;
  telephone?: string;
  email?: string;
  numero_dossier?: string;
}

export default function NouveauRendezVousPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [medecinId, setMedecinId] = useState<string | null>(null);
  const [checkingPatient, setCheckingPatient] = useState(false);
  const [dossierNotFound, setDossierNotFound] = useState(false);

  const [numeroDossier, setNumeroDossier] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientInfo | null>(null);
  const [showCreerPatientModal, setShowCreerPatientModal] = useState(false);

  const [dateRdv, setDateRdv] = useState('');
  const [heureDebut, setHeureDebut] = useState('');
  const [heureFin, setHeureFin] = useState('');
  const [motif, setMotif] = useState('');
  const [typeConsultation, setTypeConsultation] = useState('cabinet');
  const { mutate } = useOfflineMutation<any>();

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (role !== 'medecin' && role !== 'super_admin') {
      navigate('/', { replace: true });
      return;
    }

    const loadMedecinInfo = async () => {
      try {
        const { data: medecinData, error } = await supabase
          .from('medecins')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (error) throw error;
        if (medecinData) {
          setMedecinId(medecinData.id);
        }
      } catch (error) {
        console.error('Erreur lors du chargement:', error);
      }
    };

    loadMedecinInfo();
  }, [user, role, authLoading, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const dossier = numeroDossier.trim().toUpperCase();
      if (dossier) {
        searchPatientByDossier(dossier);
      } else {
        setSelectedPatient(null);
        setDossierNotFound(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [numeroDossier]);

  const searchPatientByDossier = async (dossier: string) => {
    setCheckingPatient(true);
    setDossierNotFound(false);

    const { data, error } = await supabase
      .from('patients')
      .select('id, nom, prenom, date_naissance, telephone, email, numero_dossier')
      .ilike('numero_dossier', dossier)
      .maybeSingle();

    if (!error && data) {
      setSelectedPatient(data);
      setDossierNotFound(false);
    } else {
      setSelectedPatient(null);
      if (dossier.length >= 2) {
        setDossierNotFound(true);
      } else {
        setDossierNotFound(false);
      }
    }
    setCheckingPatient(false);
  };

  const handlePatientCreated = (patientId: string) => {
    supabase
      .from('patients')
      .select('id, nom, prenom, date_naissance, telephone, email, numero_dossier')
      .eq('id', patientId)
      .single()
      .then(({ data, error }) => {
        if (!error && data) {
          setSelectedPatient(data);
          setShowCreerPatientModal(false);
        }
      });
  };

  const createAppointment = async () => {
    if (!selectedPatient || !medecinId || !dateRdv || !heureDebut || !heureFin || !motif) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const now = new Date();
    const appointmentDateTime = new Date(dateRdv + 'T' + heureDebut);
    if (appointmentDateTime <= now) {
      alert('Impossible de créer un rendez-vous dans le passé. Veuillez choisir une date et heure futures.');
      return;
    }

    try {
      setLoading(true);
      const online = navigator.onLine;

      // Vérifications (seulement en ligne)
      if (online) {
      const { data: existingRdv } = await supabase
        .from('rendez_vous')
        .select('id')
        .eq('medecin_id', medecinId)
        .eq('date_rdv', dateRdv)
        .eq('heure_debut', heureDebut)
        .maybeSingle();

      if (existingRdv) {
        alert('Un rendez-vous existe déjà à ce créneau horaire. Veuillez choisir une autre heure.');
        setLoading(false);
        return;
      }
      } // fin if (online)

      const rdvData = {
        medecin_id: medecinId,
        patient_id: selectedPatient.id,
        date_rdv: dateRdv,
        heure_debut: heureDebut,
        heure_fin: heureFin,
        motif,
        type_consultation: typeConsultation,
        statut: 'en_attente'
      };

      await mutate({
        table: 'rendez_vous',
        operation: 'INSERT',
        data: rdvData,
        metadata: {
          summary: `RDV ${selectedPatient.prenom} ${selectedPatient.nom} - ${dateRdv} ${heureDebut}`,
          performedBy: role || 'medecin',
        },
        onlineExecutor: async () => {
          const { data: inserted, error: rdvError } = await supabase
            .from('rendez_vous')
            .insert(rdvData)
            .select()
            .single();
          if (rdvError) throw rdvError;
          return inserted;
        },
      });

      const msg = online
        ? 'Rendez-vous créé avec succès !'
        : 'Rendez-vous enregistré en mode hors-ligne. Synchronisation automatique au retour de la connexion.';
      alert(msg);
      navigate('/medecin/rendez-vous', { replace: true });
    } catch (error) {
      console.error('Erreur lors de la création du rendez-vous:', error);
      alert('Erreur lors de la création du rendez-vous');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-clinic-blue mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mt-4 mb-2">Nouveau Rendez-vous</h1>
          <p className="text-gray-600">Créer un rendez-vous pour un patient</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            <i className="ri-folder-user-line text-clinic-blue mr-2"></i>
            Identification du patient
          </h2>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Numéro de dossier <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={numeroDossier}
                onChange={(e) => setNumeroDossier(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm uppercase tracking-wider"
                placeholder="Ex: CSF1393"
                autoComplete="off"
              />
              {checkingPatient && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <i className="ri-loader-4-line text-xl text-clinic-blue animate-spin"></i>
                </div>
              )}
            </div>
          </div>

          {checkingPatient && (
            <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl flex items-center gap-3">
              <i className="ri-loader-4-line text-xl text-blue-600 animate-spin"></i>
              <p className="text-blue-800 font-medium">Recherche du patient...</p>
            </div>
          )}

          {dossierNotFound && !checkingPatient && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
              <div className="flex items-start gap-3">
                <i className="ri-error-warning-line text-xl text-red-600 mt-0.5"></i>
                <div>
                  <p className="text-red-800 font-semibold mb-1">Aucun patient trouvé</p>
                  <p className="text-red-700 text-sm">
                    Aucun patient avec le numéro de dossier <strong>{numeroDossier}</strong> n'a été trouvé.
                    Vérifiez le numéro ou créez d'abord le patient.
                  </p>
                </div>
              </div>
            </div>
          )}

          {selectedPatient && !checkingPatient && (
            <div className="p-6 bg-clinic-blue/10 border-2 border-clinic-blue/30 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-clinic-blue-dark mb-3">Patient identifié</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-white p-3 rounded-xl">
                      <p className="text-xs text-gray-500 mb-0.5">Nom complet</p>
                      <p className="font-bold text-gray-900 text-lg">
                        {selectedPatient.prenom} <span className="uppercase">{selectedPatient.nom}</span>
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-xl">
                      <p className="text-xs text-gray-500 mb-0.5">Numéro de dossier</p>
                      <p className="font-bold text-gray-900 text-lg">{selectedPatient.numero_dossier}</p>
                    </div>
                    <div className="bg-white p-3 rounded-xl">
                      <p className="text-xs text-gray-500 mb-0.5">Date de naissance</p>
                      <p className="font-semibold text-gray-900">
                        {new Date(selectedPatient.date_naissance + 'T00:00:00').toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    {selectedPatient.telephone && (
                      <div className="bg-white p-3 rounded-xl">
                        <p className="text-xs text-gray-500 mb-0.5">Téléphone</p>
                        <p className="font-semibold text-gray-900">{selectedPatient.telephone}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!selectedPatient && !checkingPatient && !dossierNotFound && (
            <div className="p-4 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl flex items-center gap-3">
              <i className="ri-search-line text-xl text-gray-400"></i>
              <p className="text-gray-500 text-sm">
                Saisissez un numéro de dossier pour rechercher le patient
              </p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            <i className="ri-calendar-check-line text-clinic-blue mr-2"></i>
            Informations du rendez-vous
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date du rendez-vous <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateRdv}
                onChange={(e) => setDateRdv(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Heure de début <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={heureDebut}
                  onChange={(e) => setHeureDebut(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Heure de fin <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={heureFin}
                  onChange={(e) => setHeureFin(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motif de consultation <span className="text-red-500">*</span>
              </label>
              <textarea
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent resize-none"
                placeholder="Décrivez le motif de la consultation..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">{motif.length}/500 caractères</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de consultation <span className="text-red-500">*</span>
              </label>
              <select
                value={typeConsultation}
                onChange={(e) => setTypeConsultation(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent cursor-pointer"
              >
                <option value="cabinet">Au cabinet</option>
                <option value="domicile">À domicile</option>
                <option value="teleconsultation">Téléconsultation</option>
              </select>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                onClick={() => navigate('/medecin/rendez-vous')}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                Annuler
              </button>
              <button
                onClick={createAppointment}
                disabled={loading || !selectedPatient || !medecinId}
                className="flex-1 bg-clinic-blue text-white px-6 py-3 rounded-lg hover:bg-clinic-blue-dark transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Création en cours...
                  </>
                ) : (
                  <>
                    <i className="ri-calendar-check-line mr-2"></i>
                    Créer le rendez-vous
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </main>

      <CreerPatientModal
        isOpen={showCreerPatientModal}
        onClose={() => setShowCreerPatientModal(false)}
        onSuccess={handlePatientCreated}
      />

      <Footer />
    </div>
  );
}